import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import SiteNav from '@/components/SiteNav';

/**
 * Landing spot for people handed off from a sibling app (ChurchConnect).
 *
 * They already proved who they are over there, so they should arrive here
 * signed in — not looking at a signup form asking them to make a second
 * account for the same organisation.
 */
export default function SsoLanding() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { ssoLogin } = useAuth();
  const [error, setError] = useState('');
  // React 18 StrictMode mounts effects twice in development; the token is
  // single-use, so a second exchange would fail and show a false error.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const token = params.get('token');
    if (!token) {
      setError('That link is missing its sign-in code.');
      return;
    }
    ssoLogin(token)
      .then(() => navigate('/portal', { replace: true }))
      .catch((err) => {
        setError(
          err?.response?.data?.detail ||
            'That sign-in link is invalid or has expired. Please start again from your dashboard.'
        );
      });
  }, [params, ssoLogin, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-6 pt-24 pb-10" data-testid="sso-page">
      <SiteNav />
      <div className="glass-card p-8 text-center max-w-md w-full">
        {error ? (
          <>
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>We couldn&apos;t sign you in</h1>
            <p className="text-slate-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center justify-center min-h-[44px] px-5 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white font-semibold"
            >
              Sign in to EasyPeazy
            </button>
          </>
        ) : (
          <>
            <div className="w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-300">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
}
